import { createAsyncThunk } from "@reduxjs/toolkit"
import { ExclusionState, UploadedFile } from "../../models/file"
import query from "../../utils/query"
import { endpoints } from "./endpoints"
import { fetchApi, authHeaders, fileToBody } from "./http"

interface FileApiResponse<T> {
    status: number
    data: T
    error?: string
}

export type UploadFilePayload = {
    file: File
    token: string
    tenantId?: string
    uploadType?: "user" | "tenant"
}

export type ListFilesPayload = {
    token: string
}

export type DeleteFilePayload = {
    fileId: string
    token: string
}

export const uploadFile = createAsyncThunk<FileApiResponse<UploadedFile | null>, UploadFilePayload>(
    "files/upload",
    async ({ file, token, tenantId, uploadType }) => {
        const { body, contentType } = await fileToBody(file)

        let url: string
        if (uploadType === "tenant") {
            url = query(`${endpoints.files}/tenant`, ["filename", encodeURIComponent(file.name)])
        } else {
            url = query(endpoints.files, ["filename", encodeURIComponent(file.name)])
        }

        const result = await fetchApi<UploadedFile>(url, {
            method: "POST",
            headers: authHeaders(token, contentType),
            body,
        })

        if (!result.ok) {
            return { status: result.status, data: null, error: result.error }
        }

        return { status: result.status, data: result.data }
    },
)

export const listFiles = createAsyncThunk<FileApiResponse<UploadedFile[]>, ListFilesPayload>(
    "files/list",
    async ({ token }) => {
        const url = endpoints.files

        const result = await fetchApi<UploadedFile[]>(url, {
            method: "GET",
            headers: authHeaders(token, "application/json"),
        })

        if (!result.ok) {
            return { status: result.status, data: [], error: result.error }
        }

        return { status: result.status, data: result.data }
    },
)

export const deleteFile = createAsyncThunk<FileApiResponse<undefined>, DeleteFilePayload>(
    "files/delete",
    async ({ fileId, token }) => {
        const url = `${endpoints.files}/${fileId}`

        const result = await fetchApi<undefined>(url, {
            method: "DELETE",
            headers: authHeaders(token),
        })

        if (!result.ok) {
            return { status: result.status, data: undefined, error: result.error }
        }

        return { status: result.status, data: undefined }
    },
)

export function getFileDownloadUrl(fileId: string, token: string): string {
    return query(`${endpoints.files}/${fileId}/download`, ["token", encodeURIComponent(token)])
}

export function getFileEventsUrl(token: string): string {
    return query(endpoints.filesEvents, ["token", encodeURIComponent(token)])
}

export async function validateSources(
    urls: string[],
    token: string,
): Promise<Record<string, boolean>> {
    const result = await fetchApi<Record<string, boolean>>(endpoints.filesValidateSources, {
        method: "POST",
        headers: authHeaders(token, "application/json"),
        body: JSON.stringify({ urls }),
    })

    if (!result.ok) {
        return {}
    }

    return result.data
}

export async function validateFileIds(
    fileIds: string[],
    token: string,
): Promise<Record<string, boolean>> {
    const result = await fetchApi<Record<string, boolean>>(endpoints.filesValidateFileIds, {
        method: "POST",
        headers: authHeaders(token, "application/json"),
        body: JSON.stringify({ fileIds }),
    })

    if (!result.ok) {
        return {}
    }

    return result.data
}

export const EXCLUSION_REQUEST_TIMEOUT_MS = 15000

async function exclusionRequest(init: RequestInit): Promise<ExclusionState> {
    const controller = new AbortController()
    let timer: ReturnType<typeof setTimeout> | undefined
    try {
        const result = await Promise.race([
            fetchApi<ExclusionState>(`${endpoints.adminExcludedFiles}?versioned=true`, {
                ...init,
                cache: "no-store",
                signal: controller.signal,
            }),
            new Promise<never>((_, reject) => {
                timer = setTimeout(() => {
                    reject(new Error("Exclusion request timed out; its outcome is unknown"))
                    controller.abort()
                }, EXCLUSION_REQUEST_TIMEOUT_MS)
            }),
        ])
        if (!result.ok) throw new Error(result.error || "Exclusion request failed")
        const data = result.data
        // An old API returning an array is not safe for timeout recovery.
        if (
            !data ||
            !Array.isArray(data.excluded_file_ids) ||
            data.excluded_file_ids.some((id) => typeof id !== "string") ||
            typeof data.revision !== "string" ||
            !data.revision
        ) {
            throw new Error("The API does not support versioned exclusion saves")
        }
        return data
    } finally {
        clearTimeout(timer)
    }
}

export async function getExcludedFiles(token: string): Promise<ExclusionState> {
    return exclusionRequest({ method: "GET", headers: authHeaders(token, "application/json") })
}

export async function updateExcludedFiles(
    excludedFileIds: string[],
    token: string,
    revision: string,
): Promise<ExclusionState> {
    return exclusionRequest({
        method: "PUT",
        headers: authHeaders(token, "application/json"),
        body: JSON.stringify({ excluded_file_ids: excludedFileIds, revision }),
    })
}

export async function refreshSourceUrl(expiredUrl: string, token: string): Promise<string> {
    const result = await fetchApi<{ url: string }>(endpoints.filesRefreshSourceUrl, {
        method: "POST",
        headers: authHeaders(token, "application/json"),
        body: JSON.stringify({ url: expiredUrl }),
    })

    if (!result.ok) {
        throw new Error("Failed to refresh source URL")
    }

    return result.data.url
}
